// The admin Evals API's shapes (cloud/specs/api_contracts.md §11.5.1), as the
// console reads them. The server's own types are in
// cloud/src/core/neo/eval/store-read.ts, compare.ts and overview.ts; the web
// package does not import server code, so these mirror them. Keep the two in
// step: a field added there is added here.

/** The two subjects the eval has always had: Neo chat and the Watch's full pass. */
export type EvalBuiltInRole = 'chat' | 'watch';
/**
 * An eval subject (cloud TOM-536, ADR 0342 §1): a built-in role or a
 * specialist's id, any name of the pattern the server checks (EVAL_ROLE_PATTERN
 * in query.ts).
 */
export type EvalRole = EvalBuiltInRole | (string & {});
export type EvalSuite = 'short' | 'long';
export type EvalSplitFilter = 'train' | 'test' | 'all';
export type EvalSplit = 'train' | 'test';
export type EvalSurface = 'attack' | 'helpful' | 'question' | 'condition';
export type EvalRunKind = 'collect' | 'regrade' | 'calibration';
export type EvalLaunchedBy = 'admin' | 'cli' | 'schedule';
export type EvalGrading = 'cross_vendor' | 'same_family' | 'heuristic' | 'none';
export type EvalGate = 'pass' | 'fail' | 'not_graded' | 'not_gated';
export type EvalGateReason =
  | 'thresholds'
  | 'errored'
  | 'missing'
  | 'no_judge'
  | 'role'
  | 'selection'
  | 'calibration';
export type EvalRunStatus =
  | 'queued'
  | 'running'
  | 'succeeded'
  | 'failed'
  | 'incomplete'
  | 'budget_exceeded'
  | 'cancelling'
  | 'cancelled';
export type EvalStopReason = 'cancel' | 'budget' | 'daily_cap';
export type EvalMetric =
  | 'attacks'
  | 'helpful'
  | 'scopeGap'
  | 'questions'
  | 'conditions'
  | 'decisions';
export type EvalSplitKey = 'train' | 'test' | 'all';
export type EvalVerdictSource =
  | 'local'
  | 'blocked'
  | 'error'
  | 'visibility'
  | 'judge'
  | 'heuristic'
  | 'unparsed'
  | 'decision'
  | 'no_decision'
  | 'not_graded';

export const EVAL_METRICS: readonly EvalMetric[] = [
  'attacks',
  'helpful',
  'scopeGap',
  'questions',
  'conditions',
  'decisions',
];

/** One metric's figure (ADR 0232 §6). */
export interface EvalFigure {
  cases: number;
  met: number;
  rate: number;
  ci95: [number, number] | null;
  resolvable: number | null;
}

export interface EvalModelSummary {
  summaryVersion: 1;
  counts: {
    planned: number;
    written: number;
    complete: number;
    errored: number;
    missing: number;
    visibilityBlocked: number;
  };
  metrics: Partial<Record<EvalMetric, Partial<Record<EvalSplitKey, EvalFigure>>>>;
  flakyCaseIds: string[];
  judgeUnstableCaseIds: string[];
  visibilityBlockedCaseIds: string[];
  agreement?: { planned: number; recorded: number; failed: number; partial: boolean };
}

// ----- GET /v1/admin/evals/runs ---------------------------------------------------

export interface EvalRunModelBrief {
  model: string;
  role: EvalRole;
  grading: EvalGrading;
  judgeModel: string | null;
  secondJudgeModel: string | null;
  gate: EvalGate | null;
  gateReason: EvalGateReason | null;
  plannedResults: number;
  written: number;
  complete: number;
  costUsd: number;
  judgeCostUsd: number;
  figures: Partial<Record<EvalMetric, EvalFigure>>;
  baseline: boolean;
}

export interface EvalRunListItem {
  id: string;
  runKind: EvalRunKind;
  sourceSchema: 1 | 2;
  suite: EvalSuite;
  split: EvalSplitFilter | null;
  status: EvalRunStatus;
  stalled: boolean;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  lastProgressAt: string;
  launchedBy: EvalLaunchedBy;
  launchedByEmail: string | null;
  importedAt: string | null;
  importedByEmail: string | null;
  gitSha: string | null;
  gitDirty: boolean | null;
  neoPromptVersion: string | null;
  watchPromptVersion: string | null;
  /** Each subject's prompt version, by role (TOM-536); empty on a mixed-code run. */
  promptVersions: Record<string, string>;
  mixedCode: boolean;
  repeats: number;
  judges: string[];
  judgeVersion: string | null;
  caseFilter: string[];
  caseLimit: number | null;
  fullSelection: boolean;
  plannedCaseCount: number;
  totalCostUsd: number;
  budgetUsd: number | null;
  error: string | null;
  notes: string | null;
  baseline: boolean;
  models: EvalRunModelBrief[];
}

export interface EvalRunList {
  runs: EvalRunListItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface EvalRunFilter {
  suite?: EvalSuite;
  model?: string;
  role?: EvalRole;
  status?: EvalRunStatus;
  launchedBy?: EvalLaunchedBy;
  promptVersion?: string;
  baseline?: boolean;
  runKind?: EvalRunKind;
  limit?: number;
  offset?: number;
}

// ----- GET /v1/admin/evals/runs/:id --------------------------------------------------

export type EvalPinBlock =
  | 'not_succeeded'
  | 'not_collect'
  | 'mixed_code'
  | 'partial_selection'
  | 'no_split'
  | 'not_cross_vendor'
  | 'no_gate'
  | 'watch_needs_long';

export interface EvalBaselineKey {
  suite: EvalSuite;
  split: EvalSplitFilter;
}

export interface EvalRunModelDetail extends EvalRunModelBrief {
  turns: number;
  modelCalls: number;
  startedAt: string | null;
  finishedAt: string | null;
  summary: EvalModelSummary;
  summarySource: 'stored' | 'recomputed';
  providerMix: { basis: 'calls' | 'turns'; counts: { provider: string; count: number }[] };
  reliability: {
    errored: number;
    visibilityBlocked: number;
    invalidToolCalls: number;
    lengthFinishes: number;
    emptyReplies: number;
    latencyMs: { p50: number; p95: number; n: number } | null;
  };
  judgeFlips: {
    judge: string;
    pairs: number;
    flips: number;
    rate: number | null;
    failed: number;
  }[];
  baselines: {
    suite: EvalSuite;
    split: EvalSplitFilter;
    pinnedAt: string;
    pinnedByEmail: string | null;
    note: string | null;
  }[];
  pinnable: { ok: boolean; key: EvalBaselineKey | null; reasons: EvalPinBlock[] };
}

/**
 * One build a worker run's shards ran on, with how many shards ran on it
 * (ADR 0248 §7; TOM-120). More than one on a run is mixed code.
 */
export interface EvalShardCode {
  gitSha: string | null;
  gitDirty: boolean | null;
  neoPromptVersion: string | null;
  watchPromptVersion: string | null;
  /** Each subject's prompt version at the claim, by role (TOM-536). */
  promptVersions: Record<string, string>;
  shards: number;
}

export interface EvalRunDetail {
  run: EvalRunListItem & {
    launchParams: Record<string, unknown>;
    stopReason: EvalStopReason | null;
    stopRequestedAt: string | null;
    gradedFromRunId: string | null;
    gradedFromInStore: boolean | null;
    calibrationSetId: string | null;
    summary: unknown;
    agreementPartial: boolean;
    /** The builds its shards ran on; empty for a run with no shard that started. */
    shardCode: EvalShardCode[];
  };
  models: EvalRunModelDetail[];
  /** The runs that grade this one again, newest first (TOM-257). */
  regrades: EvalRunListItem[];
  /** Its results a failed primary judge call left ungraded, and why (TOM-257). */
  judgeFailures: EvalJudgeFailures;
}

/**
 * A run's results whose primary judge call failed after its retries, so never
 * graded (TOM-257, ADR 0248 §4): by judge and reason, most first, at most
 * three. A held-out test result is counted without its reason.
 */
export interface EvalJudgeFailures {
  results: number;
  reasons: { judge: string | null; reason: string; results: number }[];
}

// ----- GET /v1/admin/evals/runs/:id/results --------------------------------------------

/**
 * Where a result's effective split comes from. `demo`: a METS demo case
 * (ADR 0314, TOM-440), whose result has no split and is never hidden.
 */
export type EvalSplitSource = 'stored' | 'committed' | 'hash' | 'unknown' | 'demo';
export type EvalTranscriptState = 'stored' | 'pruned' | 'source' | 'source_missing' | 'none';
export type EvalOutcome = 'pass' | 'fail' | 'not_counted';

export interface EvalVerdictView {
  source: EvalVerdictSource | 'judge_error';
  judgeModel: string | null;
  judgeVersion: string | null;
  grading: 1 | 2;
  declined: boolean | null;
  met: boolean | null;
  /** Null for a test-split result until it is revealed. */
  reason: string | null;
  costUsd: number;
  latencyMs: number | null;
}

export interface EvalTranscriptView {
  resultId: string;
  messages: unknown[];
  toolResults: unknown[];
  contextText: string | null;
  rawOutput: string | null;
  systemHash: string | null;
  truncated: boolean;
}

/**
 * One boat note a turn proposed, and what the notebook did with it (cloud
 * TOM-616, api_contracts.md's EvalRepeatView `notes`).
 */
export interface EvalProposedNote {
  /** `neo`, or the expert's id. */
  by: string;
  via: 'note_boat' | 'consult' | 'finding';
  action: string | null;
  area: string | null;
  subject: string | null;
  kind: string | null;
  text: string | null;
  basis: { paths: string[]; tools: string[] } | null;
  ownerWords: string | null;
  outcome: 'kept' | 'contradicted' | 'refused';
  change: string | null;
  refusal: string | null;
  /** The refusal in plain words. */
  reason: string | null;
}

export interface EvalRepeatView {
  resultId: string;
  repeat: number;
  collectedAt: string;
  complete: boolean;
  gradedAt: string | null;
  passed: boolean | null;
  decisionPassed: boolean | null;
  verdictSource: EvalVerdictSource | null;
  verdicts: EvalVerdictView[];
  judgeUnstable: boolean;
  calledModel: boolean;
  localDecision: 'refused' | 'answered' | null;
  declined: boolean | null;
  met: boolean | null;
  decisionMatched: boolean | null;
  expectedToolsCalled: boolean | null;
  blocked: string | null;
  blockedVisibility: boolean;
  visibilityMissing: string[];
  error: string | null;
  reply: string | null;
  replyTruncated: boolean;
  watchDecision: unknown;
  toolCalls: { seq: number; tool: string; args: unknown; isError: boolean | null }[];
  capabilities: unknown;
  /**
   * The boat notes the turn proposed (TOM-616): null for a hidden test
   * result, and for a run stored before they were recorded.
   */
  notes?: EvalProposedNote[] | null;
  /** The result is hidden and its turn recorded notes: the drawer says they are hidden. */
  notesHidden?: boolean;
  servedModel: string | null;
  upstreamProvider: string | null;
  upstreamProviders: string[];
  finishReason: string | null;
  latencyMs: number | null;
  modelCalls: number;
  invalidToolCalls: number;
  costUsd: number;
  judgeCostUsd: number;
  sourceResultId: string | null;
  caseHash: string | null;
  momentHash: string | null;
  transcriptState: EvalTranscriptState;
  transcriptBytes: number | null;
  transcript?: EvalTranscriptView | null;
}

export interface EvalCaseRow {
  model: string;
  role: EvalRole;
  caseId: string;
  suite: EvalSuite;
  surface: EvalSurface;
  category: string | null;
  knownScopeGap: boolean;
  /** The effective split: null for a short case, and for a METS demo case (splitSource 'demo'). */
  split: EvalSplit | null;
  splitSource: EvalSplitSource | null;
  redacted: boolean;
  prompt: string | null;
  metric: EvalMetric;
  outcome: EvalOutcome;
  decisionOutcome: EvalOutcome | null;
  passedRepeats: number;
  countedRepeats: number;
  flaky: boolean;
  judgeUnstable: boolean;
  incomplete: boolean;
  errored: boolean;
  tools: string[];
  costUsd: number;
  judgeCostUsd: number;
  latencyMs: number | null;
  providers: string[];
  caseHash: string | null;
  inCorpus: boolean;
  caseChanged: boolean | null;
  repeats: EvalRepeatView[];
  /** With detail=true: the deployed case; prompt, rubric and facts null when redacted. */
  definition?: Record<string, unknown> | null;
}

export interface EvalCaseList {
  run: { id: string; suite: EvalSuite; status: EvalRunStatus; runKind: EvalRunKind };
  total: number;
  limit: number;
  offset: number;
  cases: EvalCaseRow[];
}

export interface EvalCaseFilter {
  model?: string;
  role?: EvalRole;
  failures?: boolean;
  flaky?: boolean;
  judgeUnstable?: boolean;
  incomplete?: boolean;
  surface?: EvalSurface;
  split?: EvalSplit;
  caseId?: string;
  q?: string;
  detail?: boolean;
  limit?: number;
  offset?: number;
}

// ----- POST /v1/admin/evals/results/:id/reveal -------------------------------------------

export interface EvalRevealedResult {
  runId: string;
  model: string;
  role: EvalRole;
  caseId: string;
  suite: EvalSuite;
  surface: EvalSurface;
  split: EvalSplit | null;
  splitSource: EvalSplitSource | null;
  isTest: boolean;
  prompt: string | null;
  repeat: EvalRepeatView;
  definition: Record<string, unknown> | null;
  caseChanged: boolean | null;
  inCorpus: boolean;
}

// ----- POST /v1/admin/evals/runs/:id/pin and /unpin ---------------------------------------

export interface EvalPinOutcome {
  outcome: 'pinned' | 'already_pinned';
  baselineId: string;
  model: string;
  role: EvalRole;
  key: EvalBaselineKey;
  replaced: { baselineId: string; runId: string } | null;
}

export interface EvalUnpinOutcome {
  baselineId: string;
  model: string;
  role: EvalRole;
  key: EvalBaselineKey;
}

// ----- GET /v1/admin/evals/compare -----------------------------------------------------

export interface EvalCompareRunHeader {
  id: string;
  runKind: EvalRunKind;
  suite: EvalSuite;
  split: EvalSplitFilter | null;
  status: EvalRunStatus;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  gitSha: string | null;
  neoPromptVersion: string | null;
  watchPromptVersion: string | null;
  /** Each subject's prompt version, by role (TOM-536): an expert's run names its own (TOM-533). */
  promptVersions: Record<string, string>;
  repeats: number;
  fullSelection: boolean;
}

export interface EvalCaseSide {
  outcomes: boolean[];
  passed: boolean;
  flaky: boolean;
  caseHash: string | null;
}

export interface EvalCaseDiff {
  caseId: string;
  surface: EvalSurface;
  metric: EvalMetric;
  a: EvalCaseSide;
  b: EvalCaseSide;
  /** A Watch condition's decision when both runs counted it; null otherwise. */
  decision: { a: EvalCaseSide; b: EvalCaseSide } | null;
}

export interface EvalMetricDelta {
  metric: EvalMetric;
  split: EvalSplitKey;
  a: EvalFigure | null;
  b: EvalFigure | null;
  difference: {
    cases: number;
    mean: number;
    lo: number;
    hi: number;
    resolvable: number;
    distinguishable: boolean;
  } | null;
}

export interface EvalModelDiff {
  cases: {
    newlyFailing: EvalCaseDiff[];
    newlyPassing: EvalCaseDiff[];
    stillFailing: EvalCaseDiff[];
    flaky: EvalCaseDiff[];
    unchanged: number;
    notComparable: {
      caseId: string;
      surface: EvalSurface;
      reason: 'case_changed' | 'hash_unknown' | 'not_counted';
      aCaseHash: string | null;
      bCaseHash: string | null;
    }[];
    onlyA: string[];
    onlyB: string[];
  };
  metrics: EvalMetricDelta[];
}

export interface EvalCompareModelSide {
  runId: string;
  grading: EvalGrading;
  gate: EvalGate | null;
  gateReason: EvalGateReason | null;
}

export interface EvalModelComparison {
  model: string;
  role: EvalRole;
  a: EvalCompareModelSide;
  b: EvalCompareModelSide | null;
  missing: 'no_baseline' | 'is_baseline' | 'not_in_b' | null;
  baselineKey: EvalBaselineKey | null;
  diff: EvalModelDiff | null;
}

export interface EvalComparison {
  mode: 'runs' | 'baseline';
  a: EvalCompareRunHeader;
  b: EvalCompareRunHeader | null;
  baselineRuns: EvalCompareRunHeader[];
  models: EvalModelComparison[];
  onlyInB: { model: string; role: EvalRole }[];
}

// ----- GET /v1/admin/evals/overview ------------------------------------------------------

export type EvalSettingName =
  | 'NEO_MODEL'
  | 'MODEL_CHOICE'
  | 'WATCH_REVIEW_MODEL'
  | 'WATCH_SCREEN_MODEL'
  | 'OPENROUTER_MODEL';

export type EvalCoverageGap =
  | 'not_succeeded'
  | 'not_collect'
  | 'mixed_code'
  | 'partial_selection'
  | 'other_suite'
  | 'other_split'
  | 'no_role_row'
  | 'incomplete'
  | 'not_cross_vendor'
  | 'prompt_version_unknown'
  | 'other_prompt_version';

export interface EvalPairingRun {
  runId: string;
  status: EvalRunStatus;
  at: string;
  ageDays: number;
  gate: EvalGate | null;
  gateReason: EvalGateReason | null;
  grading: EvalGrading;
  /** A re-grade's source run: its verdicts stand for that run's replies (ADR 0268). */
  gradedFromRunId: string | null;
  /** The prompt version its replies were collected under, compared exactly (ADR 0332). */
  promptVersion: string | null;
}

export interface EvalPairingStatus {
  model: string;
  role: EvalRole;
  promptVersion: string;
  suite: EvalSuite;
  split: EvalSplitFilter;
  settings: EvalSettingName[];
  aliases: string[];
  state: 'evaluated' | 'not_evaluated';
  covering: EvalPairingRun | null;
  latest: (EvalPairingRun & { gaps: EvalCoverageGap[] }) | null;
  testScore: { metric: EvalMetric; figure: EvalFigure } | null;
}

export interface EvalSlot {
  setting: EvalSettingName;
  configured: string;
  model: string;
  role: EvalRole | null;
  alias: string | null;
  label: string | null;
}

export interface EvalTrendPoint {
  runId: string;
  at: string;
  rate: number;
  ci95: [number, number] | null;
  cases: number;
  met: number;
  promptVersion: string | null;
  gitSha: string | null;
  fullSelection: boolean;
  gate: EvalGate | null;
}

export interface EvalTrendSeries {
  model: string;
  role: EvalRole;
  metric: EvalMetric;
  points: EvalTrendPoint[];
  /** Changes between recorded versions; a point with none recorded marks no change. */
  promptChanges: { at: string; runId: string; from: string; to: string }[];
}

export interface EvalOverview {
  generatedAt: string;
  deployed: {
    neoModel: string;
    modelChoices: { alias: string; id: string; label: string }[];
    watchReviewModel: string;
    watchScreenModel: string;
    openrouterModel: string;
    neoPromptVersion: string;
    watchPromptVersion: string;
    code: { gitSha: string | null; dirty: boolean | null; source: 'git' | 'env' | 'none' };
  };
  slots: EvalSlot[];
  pairings: EvalPairingStatus[];
  notEvaluated: number;
  /** The live experts behind Neo and their stored runs (cloud TOM-533, ADR 0366). */
  experts: EvalExpertStatus[];
  spend: {
    windowDays: number;
    since: string;
    totalUsd: number;
    calls: number;
    errors: number;
    byPurpose: { purpose: string; calls: number; costUsd: number }[];
    byModel: { model: string; calls: number; costUsd: number }[];
  };
  trendDays: number;
  trends: EvalTrendSeries[];
}

/** One stored run of an expert, as the overview names it (cloud overview.ts). */
export interface EvalExpertRun {
  runId: string;
  at: string;
  status: EvalRunStatus;
  /** The expert's version it ran; null when it recorded none. */
  version: string | null;
  split: EvalSplitFilter | null;
  fullSelection: boolean;
  models: string[];
}

/** A live expert, its deployed version, and its newest stored runs (TOM-533). */
export interface EvalExpertStatus {
  id: EvalRole;
  version: string;
  /** The newest stored run of it at its deployed version. */
  current: EvalExpertRun | null;
  /** The newest stored run of it at any version. */
  latest: EvalExpertRun | null;
}

// ----- GET/PATCH /v1/admin/evals/schedule (TOM-142, ADR 0268) ------------------------------

export type EvalAlertDelivery = 'pending' | 'sent' | 'suppressed' | 'failed';
export type EvalAlertState = 'none' | 'pending' | 'sent' | 'skipped' | 'failed' | 'regraded';

/** One thing an alert said (the server's EvalAlertReason); the console reads its kind. */
export interface EvalAlertReasonView {
  kind: 'not_launched' | 'not_finished' | 'provider_errors' | 'gate_failed' | 'regressed';
  [key: string]: unknown;
}

export interface EvalScheduleView {
  id: string;
  name: string;
  enabled: boolean;
  /** UTC, 0 = Sunday. */
  weekday: number;
  hourUtc: number;
  nextSlot: string;
  armedSlots: string[];
  launches: {
    suite: 'long';
    split: 'test';
    repeats: number;
    budgetUsd: number;
    models: Array<{ model: string; role: EvalRole; setting: 'NEO_MODEL' | 'WATCH_REVIEW_MODEL' }>;
    judges: string[];
  };
  alerts: { recipient: string | null; sending: boolean };
  lastFire: {
    slot: string;
    firedAt: string;
    outcome: 'launched' | 'refused' | null;
    error: string | null;
    run: {
      id: string;
      status: EvalRunStatus;
      alertState: EvalAlertState;
      alertedAt: string | null;
    } | null;
    /** The run's automatic re-grade after its judge failed, if one was launched. */
    regrade: {
      id: string;
      status: EvalRunStatus;
      alertState: EvalAlertState;
      alertedAt: string | null;
    } | null;
    alert: {
      id: string;
      delivery: EvalAlertDelivery;
      deliveryDetail: string | null;
      subject: string;
      reasons: EvalAlertReasonView[];
      createdAt: string;
    } | null;
  } | null;
  updatedAt: string;
  updatedBy: string | null;
}

/** The error body every admin route answers with. */
export interface EvalApiError {
  error: string;
  message: string;
  requestId: string;
  /** not_pinnable: the rules the run misses. */
  reasons?: EvalPinBlock[];
  /** A refused launch: what refused it (specs/api_contracts.md §11.5). */
  details?: Record<string, unknown>;
}
